import React from 'react'
import styled from 'styled-components'

// Configs
import { borrowingBaseCertificateConfig } from '../_configs'

// Utils
import { calculateTotalEligible, calculateLastTotalEligible, calculateTotalMargined } from './helper'
import { formatCurrency, formatIntPercentage } from '../_helpers'

// Components
import { BBCTotalsRow, StyledBBCFormRow } from './BBCFormRow'
import { BBCSectionHeader } from './BBCSectionHeader'
import { StyledBBCFormControls } from './BBCFormControls'
import { Button } from '../_components/Button'
import { BBCDocumentsViewer } from './BBCDocumentsViewer'

const StyledBBCSummarySection = styled.div`
  outline: none;

  .bbc-summary-title {
    margin: 35px 0;
    font-size: 40px;
  }

  .section-fields {
    margin-bottom: 50px;
  }

  ${StyledBBCFormRow} {
    margin-bottom: 15px;
  }

  .last-total-value,
  .total-value {
    text-align: right;
  }

  .total-value {
    padding-left: 1em;
  }

  .section-totals-divider {
    border-top: 1px solid #ddd;
    margin: 20px 0;
  }
`

export const BBCSummary = ({
  formSections,
  assets,
  ineligibles,
  handleSectionChange,
  handleCompleteForm,
  showLast,
}) => {
  let lastGrandTotal = 0
  let grandTotal = 0

  const SummarySections = formSections.map((formSection, i) => {
    // Get ineligables for section asset
    const sectionAsset = assets[formSection.assetId]
    const sectionIneligibles = sectionAsset.ineligibles.map((ineligiblesId) => {
      return ineligibles[ineligiblesId]
    })

    // Calculate section totals
    const lastTotalEligible = calculateLastTotalEligible(sectionAsset, ineligibles)
    const totalEligible = calculateTotalEligible(sectionAsset, ineligibles)
    const advancedRate = sectionAsset.advance_rate
    const lastTotalMarginedAmount = calculateTotalMargined(lastTotalEligible, advancedRate)
    const totalMarginedAmount = calculateTotalMargined(totalEligible, advancedRate)

    lastGrandTotal += lastTotalMarginedAmount
    grandTotal += totalMarginedAmount

    return (
      <div key={sectionAsset.id}>
        <BBCSectionHeader
          colHeaders={[borrowingBaseCertificateConfig.lastSubTitle, borrowingBaseCertificateConfig.newSubTitle]}
          onEditClick={(e) => {
            handleSectionChange(i + 1)
          }}
          showEditLink={true}
          showLast={showLast}
          title={formSection.title}
        />

        <div className={`section-fields`}>
          {/* Asset Date */}
          <BBCTotalsRow
            label={'Report Date'}
            lastValue={new Date(sectionAsset.last_date).toLocaleDateString()}
            showLast={showLast}
            value={new Date(sectionAsset.date).toLocaleDateString()}
          />

          {/* Asset Value */}
          <BBCTotalsRow
            label={formSection.fields[1].label}
            lastValue={formatCurrency(sectionAsset.last_value)}
            showLast={showLast}
            value={formatCurrency(sectionAsset.value)}
          />

          {/* Section Ineligibles */}
          {sectionIneligibles.map((ineligible, i) => (
            <BBCTotalsRow
              isIneligible={true}
              key={ineligible.id}
              label={ineligible.label}
              lastValue={`(${formatCurrency(ineligible.last_value)})`}
              showLast={showLast}
              value={`(${formatCurrency(ineligible.value)})`}
            />
          ))}

          {/* Section Totals */}
          <BBCTotalsRow
            label={formSection.totalCaption}
            lastValue={formatCurrency(lastTotalEligible)}
            showLast={showLast}
            value={formatCurrency(totalEligible)}
          />
          <BBCTotalsRow
            label={formSection.rateTitle}
            lastValue={formatIntPercentage(advancedRate)}
            showLast={showLast}
            value={formatIntPercentage(advancedRate)}
          />
          <BBCTotalsRow
            label={formSection.marginedAmount}
            lastValue={formatCurrency(lastTotalMarginedAmount)}
            showLast={showLast}
            value={formatCurrency(totalMarginedAmount)}
          />
        </div>
      </div>
    )
  })

  return (
    <StyledBBCSummarySection>
      <h2 className="bbc-summary-title">Review</h2>
      {SummarySections}
      <div>
        <BBCSectionHeader
          colHeaders={[borrowingBaseCertificateConfig.lastSubTitle, borrowingBaseCertificateConfig.newSubTitle]}
          showLast={showLast}
          title={'Totals'}
        />
        <div className={`section-fields`}>
          <BBCTotalsRow
            label={'Borrowing Base Total'}
            lastValue={formatCurrency(lastGrandTotal)}
            showLast={showLast}
            value={formatCurrency(grandTotal)}
          />
        </div>

        {/* Documents (not hooked up to anything yet)
                    TODO: implement processing these docs
                    */}
        <BBCDocumentsViewer addDocument={() => {}} bbcId={1} documents={[]} fileType={'Summary'} />
      </div>
      <StyledBBCFormControls>
        <Button buttonType={`button`} onClick={handleCompleteForm} text={`Submit`} type={`button`} />
      </StyledBBCFormControls>
    </StyledBBCSummarySection>
  )
}
